/**
 * Felles hjelpere for objekt-verktøyene (søyler, porter, stasjoner, lading,
 * sonemarkører): typeliste, fornuftige standardmål per type, oversatte
 * typenavn og id-generering for nye objekter.
 */

import { WarehouseObjectType } from '../types/warehouseLayout';

/** Samme signatur som t() fra I18nProvider — unngår hook-avhengighet i ren util */
export type TranslateFn = (
  key: string,
  params?: Record<string, string | number | null | undefined>,
  defaultText?: string
) => string;

/** Rekkefølgen typene vises i (Bygg-fanen og type-velgeren) */
export const OBJECT_TYPES: WarehouseObjectType[] = [
  'pillar',
  'door',
  'station',
  'charging',
  'zone_marker',
];

/** Standardmål (meter) per objekttype ved plassering */
export const OBJECT_TYPE_DEFAULT_SIZES: Record<
  WarehouseObjectType,
  { width: number; depth: number; height: number }
> = {
  pillar:      { width: 0.4, depth: 0.4, height: 6 },
  door:        { width: 4,   depth: 0.4, height: 4.5 },
  station:     { width: 3,   depth: 1.5, height: 1.1 },
  charging:    { width: 1.5, depth: 1,   height: 1.2 },
  zone_marker: { width: 4,   depth: 4,   height: 0.05 },
};

/** i18n-nøkkel-suffiks per type (camelCase i JSON) */
const OBJECT_TYPE_KEY: Record<WarehouseObjectType, string> = {
  pillar: 'typePillar',
  door: 'typeDoor',
  station: 'typeStation',
  charging: 'typeCharging',
  zone_marker: 'typeZoneMarker',
};

const OBJECT_TYPE_FALLBACK: Record<WarehouseObjectType, string> = {
  pillar: 'Søyle',
  door: 'Port',
  station: 'Stasjon',
  charging: 'Lading',
  zone_marker: 'Sonemarkør',
};

/** Oversatt visningsnavn for en objekttype (norsk fallback) */
export function objectTypeLabel(t: TranslateFn, type: WarehouseObjectType): string {
  return t(
    `manager.modules.warehouseLayout.objects.${OBJECT_TYPE_KEY[type]}`,
    {},
    OBJECT_TYPE_FALLBACK[type]
  );
}

// Løpende teller sikrer unik id selv ved flere plasseringer i samme millisekund
let objectIdSeq = 0;

/** Genererer klient-id for nye objekter ('obj-...'; server-lastede har 'srv-...') */
export function generateObjectId(): string {
  objectIdSeq += 1;
  return `obj-${Date.now()}-${objectIdSeq}`;
}
